import React from 'react';
import Select from 'react-select';
import { Search20Regular, Dismiss20Regular } from '@fluentui/react-icons';
import { Menu, MenuTrigger, MenuPopover } from '@fluentui/react-components';
import styles from './css/index.module.css';

export const FILTER_MULTI_SELECT_STYLES = {
    control: (base, state) => ({
        ...base,
        minHeight: 32,
        fontSize: 11,
        fontFamily: 'Inter, sans-serif',
        borderColor: state.isFocused ? 'var(--Brand-Secondary, #c89741)' : '#dee2e6',
        boxShadow: 'none',
        borderRadius: 4,
        '&:hover': { borderColor: 'var(--Brand-Secondary, #c89741)' },
    }),
    valueContainer: base => ({
        ...base,
        padding: '0 4px',
        gap: 2,
    }),
    input: base => ({
        ...base,
        margin: 0,
        padding: 0,
        fontSize: 11,
    }),
    placeholder: base => ({
        ...base,
        fontSize: 11,
        color: '#6c757d',
    }),
    multiValue: base => ({
        ...base,
        margin: 1,
        backgroundColor: '#f1e9fb',
        borderRadius: 4,
    }),
    multiValueLabel: base => ({
        ...base,
        fontSize: 11,
        padding: '1px 4px',
        color: 'var(--Brand-Primary, #003048)',
    }),
    multiValueRemove: base => ({
        ...base,
        padding: '0 2px',
        ':hover': { backgroundColor: '#e4d4f7', color: 'var(--Brand-Secondary, #c89741)' },
    }),
    indicatorsContainer: base => ({
        ...base,
        height: 22,
    }),
    dropdownIndicator: base => ({
        ...base,
        padding: 2,
    }),
    clearIndicator: base => ({
        ...base,
        padding: 2,
    }),
    menu: base => ({
        ...base,
        fontSize: 12,
        zIndex: 10000000,
    }),
    menuPortal: base => ({
        ...base,
        zIndex: 10000000,
    }),
    option: (base, state) => ({
        ...base,
        fontSize: 12,
        padding: '4px 8px',
        backgroundColor: state.isSelected ? 'var(--Brand-Secondary, #c89741)' : state.isFocused ? '#f6f0fc' : base.backgroundColor,
        color: state.isSelected ? '#fff' : base.color,
    }),
};

export const isFilterSelectMenuTarget = target =>
    target instanceof Element && Boolean(target.closest('.taskFilterSelect__menu, .taskFilterSelect__menu-portal'));

export const selectedFilterValues = value => {
    if (Array.isArray(value)) return value;
    if (value) return [value];
    return [];
};

export const filterHasSelection = filter => Boolean(filter?.column) && selectedFilterValues(filter?.value).length > 0;

export const matchesSelectedValues = (actual, selectedValues) => selectedValues.includes(actual);

export const applyTextSearch = (items, query, getSearchText) => {
    const trimmed = (query || '').trim().toLowerCase();
    if (!trimmed || !items?.length) return items;
    return items.filter(item => (getSearchText(item) || '').toLowerCase().includes(trimmed));
};

export const ToolbarSearchMenu = ({ query, onQueryChange, placeholder = 'Search…', ariaLabel = 'Search' }) => {
    const [open, setOpen] = React.useState(false);
    const inputRef = React.useRef(null);

    return (
        <Menu
            open={open}
            onOpenChange={(_, data) => {
                setOpen(data.open);
                if (data.open) {
                    requestAnimationFrame(() => inputRef.current?.focus());
                }
            }}>
            <MenuTrigger disableButtonEnhancement>
                <span className={`${styles.linkText} ${query ? styles.taskSearchTriggerActive : ''}`} style={{ cursor: 'pointer' }}>
                    <Search20Regular className="me-1" />
                    Search
                </span>
            </MenuTrigger>
            <MenuPopover style={{ width: '320px', minWidth: '320px', padding: '8px' }}>
                <div
                    className={styles.taskSearchRow}
                    onKeyDown={event => {
                        if (event.key !== 'Escape') event.stopPropagation();
                    }}>
                    <Search20Regular className={styles.taskSearchIcon} aria-hidden />
                    <input
                        ref={inputRef}
                        type="search"
                        className={styles.taskSearchInput}
                        placeholder={placeholder}
                        aria-label={ariaLabel}
                        value={query}
                        onChange={event => onQueryChange(event.target.value)}
                    />
                    {query ? (
                        <button
                            type="button"
                            className={styles.taskSearchClear}
                            aria-label="Clear search"
                            onClick={() => {
                                onQueryChange('');
                                inputRef.current?.focus();
                            }}>
                            <Dismiss20Regular />
                        </button>
                    ) : null}
                </div>
            </MenuPopover>
        </Menu>
    );
};

export const FilterMultiSelect = ({ options, value, onChange, disabled }) => {
    const selectedValues = selectedFilterValues(value);

    return (
        <div
            className={styles.filterValueSelect}
            onMouseDown={event => event.stopPropagation()}
            onClick={event => event.stopPropagation()}
            onKeyDown={event => event.stopPropagation()}>
            <Select
                classNamePrefix="taskFilterSelect"
                isMulti
                isDisabled={disabled}
                options={options}
                placeholder="Select values"
                closeMenuOnSelect={false}
                hideSelectedOptions={false}
                menuPortalTarget={typeof document !== 'undefined' ? document.body : null}
                menuPosition="fixed"
                styles={FILTER_MULTI_SELECT_STYLES}
                value={options.filter(option => selectedValues.includes(option.value))}
                onChange={selected => onChange((selected || []).map(option => option.value))}
            />
        </div>
    );
};
